import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Shield, FileText, ArrowLeft, ArrowRight, Check, 
  User, Home, Phone, Mail, Calendar, Lock, 
  AlertTriangle, Pen, Download, Eye, ChevronDown,
  CheckCircle2, Heart, Briefcase, Scale
} from 'lucide-react';

// US States for dropdown
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Tax classifications
const TAX_CLASSIFICATIONS = [
  { value: 'individual', label: 'Individual / Sole Proprietor' },
  { value: 'llc_single', label: 'Single-member LLC' },
  { value: 'llc_multi', label: 'LLC (multiple members)' },
  { value: 'c_corp', label: 'C Corporation' },
  { value: 's_corp', label: 'S Corporation' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'trust', label: 'Trust / Estate' }
];

export default function SoldierAgreement() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agreementStatus, setAgreementStatus] = useState(null);
  const [agreementText, setAgreementText] = useState('');
  const [companyInfo, setCompanyInfo] = useState(null);
  const [step, setStep] = useState(1); // 1=info, 2=read, 3=sign
  const [showFullAgreement, setShowFullAgreement] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    legal_name: '',
    address: '',
    city: '',
    state: 'TX',
    zip_code: '',
    email: '',
    phone: '',
    ssn_last_four: '',
    date_of_birth: '',
    tax_classification: 'individual',
    business_name: '',
    ein: '',
    beneficiary_name: '',
    beneficiary_relationship: '',
    beneficiary_address: '',
    beneficiary_phone: '',
    beneficiary_email: '',
    signature: '',
    signature_date: new Date().toISOString().split('T')[0],
    agree_to_terms: false,
    agree_to_arbitration: false,
    agree_to_independent_contractor: false
  });

  useEffect(() => {
    if (token) {
      fetchAgreementStatus();
      fetchAgreementText();
    }
  }, [token]);

  useEffect(() => {
    // Pre-fill email and name from user data
    if (user) {
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        legal_name: user.name || ''
      }));
    }
  }, [user]);

  const fetchAgreementStatus = async () => {
    try {
      const response = await axios.get(`${API}/soldier-agreement/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreementStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch agreement status:', error);
      setLoading(false);
    }
  };

  const fetchAgreementText = async () => {
    try {
      const response = await axios.get(`${API}/soldier-agreement/text`);
      setAgreementText(response.data.agreement_text);
      setCompanyInfo(response.data.company);
    } catch (error) {
      console.error('Failed to fetch agreement text:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    const required = ['legal_name', 'address', 'city', 'state', 'zip_code', 'email', 'phone', 'ssn_last_four', 'date_of_birth'];
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in all required fields`);
        return false;
      }
    }
    if (formData.ssn_last_four.length !== 4 || !/^\d{4}$/.test(formData.ssn_last_four)) {
      toast.error('Please enter the last 4 digits of your SSN');
      return false;
    }
    if (formData.tax_classification !== 'individual' && !formData.business_name) {
      toast.error('Please enter your business name');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    const required = ['beneficiary_name', 'beneficiary_relationship', 'beneficiary_address', 'beneficiary_phone'];
    for (const field of required) {
      if (!formData[field]) {
        toast.error('Please fill in all beneficiary information');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    // Validate signature
    if (!formData.signature) {
      toast.error('Please type your legal name as your digital signature');
      return;
    }
    if (formData.signature.toLowerCase().trim() !== formData.legal_name.toLowerCase().trim()) {
      toast.error('Your signature must match your legal name exactly');
      return;
    }
    if (!formData.agree_to_terms || !formData.agree_to_arbitration || !formData.agree_to_independent_contractor) {
      toast.error('Please agree to all terms before signing');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/soldier-agreement/sign`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Agreement signed successfully!');
      
      // Refresh status and redirect to dashboard
      await fetchAgreementStatus();
      setTimeout(() => {
        navigate('/soldiers/dashboard');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign agreement');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950 flex items-center justify-center">
        <div className="text-center">
          <Scale className="w-16 h-16 text-amber-400 mx-auto mb-4 animate-pulse" />
          <p className="text-white text-lg">Loading Agreement...</p>
        </div>
      </div>
    );
  }

  // Already signed view
  if (agreementStatus?.has_signed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950">
        <div className="border-b border-amber-500/20 bg-stone-950/80 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate('/soldiers/dashboard')} className="text-stone-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Command Center
            </Button>
            <Badge className="bg-green-500/20 text-green-400">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Agreement Signed
            </Badge>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="text-center mb-8">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Agreement Signed</h1>
            <p className="text-stone-400">
              You signed the Soldier Agreement on {new Date(agreementStatus.signed_at).toLocaleDateString()}
            </p>
          </div>

          <Card className="bg-stone-900/50 border-stone-700 mb-6">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-stone-400">Agreement ID</span>
                <span className="text-white font-mono">{agreementStatus.agreement_id}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-400">Version</span>
                <span className="text-white">{agreementStatus.version}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-stone-400">Beneficiary</span>
                <span className="text-white">{agreementStatus.beneficiary_name}</span>
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/soldiers/dashboard')}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
            >
              <Shield className="w-4 h-4 mr-2" />
              Go to Command Center
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open(`${API}/soldier-agreement/download/${agreementStatus.agreement_id}`, '_blank')}
              className="border-stone-600 text-stone-300"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Agreement
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-stone-950">
      {/* Header */}
      <div className="border-b border-amber-500/20 bg-stone-950/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-stone-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-stone-700" />
            <div className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-amber-400" />
              <span className="text-white font-semibold">Soldier Agreement</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-8 h-1 rounded-full ${step >= s ? 'bg-amber-500' : 'bg-stone-700'}`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Step 1: Personal & Tax Information */}
        {step === 1 && (
          <div>
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <User className="w-8 h-8 text-amber-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Personal Information</h1>
              <p className="text-stone-400">Required for tax reporting and agreement purposes</p>
            </div>

            <Card className="bg-stone-900/50 border-stone-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-400" />
                  Your Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-stone-300">Legal Name (as it appears on tax documents) *</Label>
                  <Input
                    value={formData.legal_name}
                    onChange={(e) => handleInputChange('legal_name', e.target.value)}
                    placeholder="John Doe"
                    className="bg-stone-800 border-stone-600 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-stone-300">Street Address *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="123 Main Street"
                    className="bg-stone-800 border-stone-600 text-white mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="text-stone-300">City *</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="Houston"
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-300">State *</Label>
                    <Select value={formData.state} onValueChange={(v) => handleInputChange('state', v)}>
                      <SelectTrigger className="bg-stone-800 border-stone-600 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((st) => (
                          <SelectItem key={st} value={st}>{st}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-stone-300">ZIP *</Label>
                    <Input
                      value={formData.zip_code}
                      onChange={(e) => handleInputChange('zip_code', e.target.value)}
                      placeholder="77001"
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-300">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="you@email.com"
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-300">Phone *</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-300">Date of Birth *</Label>
                    <Input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-300">SSN (Last 4 digits) *</Label>
                    <Input
                      value={formData.ssn_last_four}
                      onChange={(e) => handleInputChange('ssn_last_four', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="1234"
                      maxLength={4}
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                    <p className="text-xs text-stone-500 mt-1">Only the last 4 digits are stored</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-stone-900/50 border-stone-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-amber-400" />
                  Tax Information (W-9)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-stone-300">Tax Classification *</Label>
                  <Select value={formData.tax_classification} onValueChange={(v) => handleInputChange('tax_classification', v)}>
                    <SelectTrigger className="bg-stone-800 border-stone-600 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TAX_CLASSIFICATIONS.map((tc) => (
                        <SelectItem key={tc.value} value={tc.value}>{tc.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.tax_classification !== 'individual' && (
                  <>
                    <div>
                      <Label className="text-stone-300">Business Name *</Label>
                      <Input
                        value={formData.business_name}
                        onChange={(e) => handleInputChange('business_name', e.target.value)}
                        placeholder="Your Business LLC"
                        className="bg-stone-800 border-stone-600 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-stone-300">EIN (Employer Identification Number)</Label>
                      <Input
                        value={formData.ein}
                        onChange={(e) => handleInputChange('ein', e.target.value)}
                        placeholder="XX-XXXXXXX"
                        className="bg-stone-800 border-stone-600 text-white mt-1"
                      />
                    </div>
                  </>
                )}

                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-medium">Tax Reporting Notice</p>
                      <p className="text-stone-400 text-sm mt-1">
                        As an independent contractor, you will receive a 1099-NEC form if your commissions exceed $600 in a calendar year. You are responsible for all applicable taxes.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                onClick={() => {
                  if (validateStep1()) setStep(2);
                }}
                className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Beneficiary Information */}
        {step === 2 && (
          <div>
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-rose-500/20 rounded-xl flex items-center justify-center">
                <Heart className="w-8 h-8 text-rose-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Beneficiary Designation</h1>
              <p className="text-stone-400">
                In case of death, your commissions will be paid to this person for up to 3 years
              </p>
            </div>

            <Card className="bg-stone-900/50 border-stone-700 mb-6">
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="text-stone-300">Beneficiary Full Name *</Label>
                  <Input
                    value={formData.beneficiary_name}
                    onChange={(e) => handleInputChange('beneficiary_name', e.target.value)}
                    placeholder="Jane Doe"
                    className="bg-stone-800 border-stone-600 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-stone-300">Relationship *</Label>
                  <Select value={formData.beneficiary_relationship} onValueChange={(v) => handleInputChange('beneficiary_relationship', v)}>
                    <SelectTrigger className="bg-stone-800 border-stone-600 text-white mt-1">
                      <SelectValue placeholder="Select relationship" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spouse">Spouse</SelectItem>
                      <SelectItem value="domestic_partner">Domestic Partner</SelectItem>
                      <SelectItem value="child">Child</SelectItem>
                      <SelectItem value="parent">Parent</SelectItem>
                      <SelectItem value="sibling">Sibling</SelectItem>
                      <SelectItem value="relative">Other Relative</SelectItem>
                      <SelectItem value="friend">Friend</SelectItem>
                      <SelectItem value="trust">Trust</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-stone-300">Beneficiary Address *</Label>
                  <Input
                    value={formData.beneficiary_address}
                    onChange={(e) => handleInputChange('beneficiary_address', e.target.value)}
                    placeholder="123 Main St, City, ST 12345"
                    className="bg-stone-800 border-stone-600 text-white mt-1"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-stone-300">Beneficiary Phone *</Label>
                    <Input
                      value={formData.beneficiary_phone}
                      onChange={(e) => handleInputChange('beneficiary_phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-stone-300">Beneficiary Email</Label>
                    <Input
                      type="email"
                      value={formData.beneficiary_email}
                      onChange={(e) => handleInputChange('beneficiary_email', e.target.value)}
                      placeholder="beneficiary@email.com"
                      className="bg-stone-800 border-stone-600 text-white mt-1"
                    />
                  </div>
                </div>

                <div className="p-4 bg-stone-800/50 border border-stone-700 rounded-lg">
                  <p className="text-stone-400 text-sm">
                    <strong className="text-white">Death Benefits:</strong> Per Article 5 of the agreement, your designated beneficiary will receive your commission payments for a period of 3 years from the date of death, provided they submit the required documentation within 90 days.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="border-stone-600 text-stone-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={() => {
                  if (validateStep2()) setStep(3);
                }}
                className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
              >
                Review & Sign
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Review and Sign */}
        {step === 3 && (
          <div>
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-xl flex items-center justify-center">
                <Pen className="w-8 h-8 text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">Review & Sign</h1>
              <p className="text-stone-400">Please review the agreement and provide your digital signature</p>
            </div>

            {/* Agreement Preview */}
            <Card className="bg-stone-900/50 border-stone-700 mb-6">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-400" />
                  Independent Contractor Agreement
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFullAgreement(!showFullAgreement)}
                  className="text-amber-400"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {showFullAgreement ? 'Collapse' : 'View Full'}
                </Button>
              </CardHeader>
              <CardContent>
                <div className={`text-stone-300 text-sm font-mono bg-stone-950 rounded-lg p-4 overflow-hidden ${showFullAgreement ? 'max-h-96 overflow-y-auto' : 'max-h-48'}`}>
                  <pre className="whitespace-pre-wrap">{agreementText}</pre>
                </div>
              </CardContent>
            </Card>

            {/* Key Terms Summary */}
            <Card className="bg-stone-900/50 border-stone-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white">Key Terms Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                  <span className="text-stone-300"><strong className="text-white">Commission:</strong> $10 signup + $10 first month + $8/month recurring</span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                  <span className="text-stone-300"><strong className="text-white">Tail Period:</strong> 3 years of commissions after voluntary separation</span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                  <span className="text-stone-300"><strong className="text-white">Death Benefits:</strong> Beneficiary receives commissions for 3 years</span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                  <span className="text-stone-300"><strong className="text-white">Jurisdiction:</strong> Texas law, Harris County arbitration</span>
                </div>
              </CardContent>
            </Card>

            {/* Acknowledgments */}
            <Card className="bg-stone-900/50 border-stone-700 mb-6">
              <CardHeader>
                <CardTitle className="text-white">Acknowledgments</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={formData.agree_to_terms}
                    onCheckedChange={(checked) => handleInputChange('agree_to_terms', checked)}
                    className="mt-1"
                  />
                  <label htmlFor="terms" className="text-stone-300 text-sm cursor-pointer">
                    I have read and agree to the <span className="text-amber-400">terms and conditions</span> of this Independent Contractor Agreement.
                  </label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="arbitration"
                    checked={formData.agree_to_arbitration}
                    onCheckedChange={(checked) => handleInputChange('agree_to_arbitration', checked)}
                    className="mt-1"
                  />
                  <label htmlFor="arbitration" className="text-stone-300 text-sm cursor-pointer">
                    I agree to the <span className="text-amber-400">mandatory arbitration clause</span> (Article 11) and waive my right to a jury trial.
                  </label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="contractor"
                    checked={formData.agree_to_independent_contractor}
                    onCheckedChange={(checked) => handleInputChange('agree_to_independent_contractor', checked)}
                    className="mt-1"
                  />
                  <label htmlFor="contractor" className="text-stone-300 text-sm cursor-pointer">
                    I acknowledge that I am an <span className="text-amber-400">independent contractor</span>, not an employee, and I am responsible for my own taxes.
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Digital Signature */}
            <Card className="bg-gradient-to-br from-stone-900 to-stone-950 border-2 border-amber-500/50 mb-6">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Pen className="w-5 h-5 text-amber-400" />
                  Digital Signature
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-stone-300">Type your legal name as your digital signature *</Label>
                  <Input
                    value={formData.signature}
                    onChange={(e) => handleInputChange('signature', e.target.value)}
                    placeholder={formData.legal_name}
                    className="bg-stone-950 border-amber-500/50 text-white mt-1 text-lg font-serif italic"
                  />
                  <p className="text-xs text-stone-500 mt-1">
                    Must match exactly: <span className="text-amber-400">{formData.legal_name}</span>
                  </p>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-stone-700">
                  <div>
                    <p className="text-stone-400 text-sm">Signature Date</p>
                    <p className="text-white font-medium">{new Date(formData.signature_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-stone-400 text-sm">Agreement Version</p>
                    <p className="text-white font-medium">1.0</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => setStep(2)}
                className="border-stone-600 text-stone-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !formData.agree_to_terms || !formData.agree_to_arbitration || !formData.agree_to_independent_contractor}
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Signing...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Sign Agreement
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
